import { Transition } from "@headlessui/react";
import { useAtom } from "jotai";
import isEmpty from "lodash/isEmpty";
import { Fragment, useEffect, useRef, useState } from "react";

import {
  Notification,
  NotificationTone,
} from "@/components/Interface/Notification/Notification";
import {
  AlertInterface,
  alertListAtom,
  AlertTypes,
  showsSessionExpiredModalAtom,
} from "@/utils/atoms";

const toneByAlertType: Record<AlertTypes, NotificationTone> = {
  [AlertTypes.error]: "error",
  [AlertTypes.info]: "info",
  [AlertTypes.success]: "success",
  [AlertTypes.warning]: "warning",
};

export const Alert = ({
  timeoutDuration = 5000,
}: {
  timeoutDuration?: number;
}) => {
  const timeout = useRef<NodeJS.Timeout>();
  const [visible, setVisible] = useState(false);
  const [currentAlert, setCurrentAlert] = useState<AlertInterface>();
  const [alertList, setAlertList] = useAtom(alertListAtom);
  const sessionExpiredModalShown = useAtom(showsSessionExpiredModalAtom)[0];

  useEffect(() => {
    if (!sessionExpiredModalShown) {
      return;
    }

    clearTimeout(timeout.current);
    setVisible(false);
    setCurrentAlert(undefined);
    setAlertList([]);
  }, [sessionExpiredModalShown, setAlertList]);

  useEffect(() => {
    if (
      sessionExpiredModalShown ||
      visible ||
      currentAlert ||
      isEmpty(alertList)
    ) {
      return;
    }

    setVisible(true);
    setAlertList(([, ...rest]) => rest);
    setCurrentAlert(alertList[0]);

    timeout.current = setTimeout(() => setVisible(false), timeoutDuration);
  }, [
    visible,
    currentAlert,
    alertList,
    setAlertList,
    timeoutDuration,
    sessionExpiredModalShown,
  ]);

  useEffect(() => () => clearTimeout(timeout.current), []);

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-x-0 top-4 z-[100] px-4 py-6 md:inset-x-20 xl:inset-x-96"
    >
      <div className="w-full">
        <Transition
          show={visible}
          as={Fragment}
          enter="duration-300 ease-out"
          enterFrom="-translate-y-full opacity-0"
          enterTo="translate-y-0 opacity-100"
          leave="duration-750 ease-out"
          leaveFrom="translate-y-0 opacity-100"
          leaveTo="-translate-y-full opacity-0"
          afterLeave={() => setCurrentAlert(undefined)}
        >
          <div>
            <Notification
              className="pointer-events-auto mx-auto w-full max-w-sm md:max-w-2xl"
              role="alertdialog"
              aria-label={currentAlert?.text}
              floating
              tone={currentAlert && toneByAlertType[currentAlert.type]}
              title={currentAlert?.text}
              onClose={() => {
                clearTimeout(timeout.current);
                setVisible(false);
              }}
            />
          </div>
        </Transition>
      </div>
    </div>
  );
};
